import { useState, useEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import Tooltip from "@mui/material/Tooltip";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridViewIcon from "@mui/icons-material/GridView";
import PageviewIcon from "@mui/icons-material/Pageview";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import SuiBadge from "components/SuiBadge";
import SuiBadgeDot from "components/SuiBadgeDot";
import ContactAvatar from "components/UpStyled/ContactAvatar";

import DataTable from "examples/Tables/DataTable";
import Actionables from "components/UpStyled/Actionables";
import NumberFormat from "react-number-format";

import toast from "react-hot-toast";
import moment from "moment";
import { capitalCase } from "capital-case";
import { useForm, Controller } from "react-hook-form";

import { AccountContext } from "context/account";
import MyProjectsContext from "context/my-projects";

import ProjectCard2 from "components/UpStyled/project-card/ProjectCard2";
import Prompt from "components/UpStyled/Prompt";
import api from "services/api";
import qs from "query-string";
import { projectSortOptions, projectStatusOptions, budgetOptions } from "utils/data";
import { handleUploadFiles, getStyleImages, canResizeImageUrl } from "utils/imageHelper";
import { compare } from "utils/helper";

import { MAX_SELECTED_COUNT } from "utils/params";
import EmptyProjectCard from "pages/de/project/components/EmptyProjectCard";
import curved9 from "assets/images/upstyled/default_background.png";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import ProjectList from "pages/de/project/my-projects";

const smartdate = require("smartdate");

const explanation = [
  {
    title: "Drafts",
    text: "A newly created project before being submitted to the manufacturers.",
    stage: "init",
  },
  {
    title: "Sent Requests",
    text: "Projects being submitted to the manufacturers and in the negotiation stage.",
    stage: "requested",
  },
  {
    title: "Ongoing",
    text: "Projects in action once you have chosen and confirmed with a final manufacturer to work on your project.",
    stage: "active",
  },
  {
    title: "Completed",
    text: "Projects and services have been completed with confirmation from both side.",
    stage: "completed",
  },
  {
    title: "Canceled",
    text: "Ongoing projects can be canceled ONLY IF both parties have agreed.",
    stage: "canceled",
  },
];

const sortKeys = ["init", "requested", "active", "completed", "canceled"];
const sortKeys_reverse = ["canceled", "completed", "active", "requested", "init"];

const ProjectHome = ({ stage = "" }) => {
  const [view, setView] = useState("table");
  const [projectList, setProjectList] = useState([]);
  const [filteredProjectList, setFilteredProjectList] = useState([]);
  const [totalNumber, setTotalNumber] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [orderOpt, setOrderOpt] = useState(projectSortOptions[3]);
  const [searchStr, setSearchStr] = useState("");
  const [chats, setChats] = useState(new Map());
  const [selectedProject, setSelectedProject] = useState({});
  const [sendRequestVisible, setSendRequestVisible] = useState(false);
  const [deleteProjectVisible, setDeleteProjectVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState([]);

  const navigate = useNavigate();

  smartdate.init();

  const account = useContext(AccountContext);
  const {
    stagesProjects,
    prospects,
    removeMyFavoriteProject,
    addUnFavoriteProject,
    handleRefresh,
  } = useContext(MyProjectsContext);

  let location = useLocation();

  const url = location.href ? location.href : location.pathname + location.search;
  const urlObj = qs.parseUrl(url);
  const projectId = urlObj?.query?.id;

  const hint = explanation.find((x) => x.stage == stage);

  useEffect(() => {
    handleRefresh();
  }, []);
  useEffect(() => {
    if (account.id) {
      retrieveProjects(stage);
      retrieveChats();
    }
  }, [account, url, prospects, stagesProjects]);

  const retrieveProjects = async (stage) => {
    let projects = [];
    if (stage == "prospects") {
      projects = prospects;
    } else {
      projects = stagesProjects[stage] || [];
    }
    const totalNumber = projects.length;
    setTotalNumber(totalNumber);

    const sortedProjects = sortByProjectStatus(projects, sortKeys);

    setProjectList(sortedProjects);
    //filter based on pagination
    const sliceStart = pageSize * (page - 1);
    const sliceEnd = pageSize * page > totalNumber ? totalNumber : pageSize * page;
    const newFilteredList = sortedProjects.slice(sliceStart, sliceEnd);
    setFilteredProjectList(newFilteredList);
  };

  const retrieveChats = async () => {
    try {
      api.chatroom.listChatRoom("project", "", "").then(({ data }) => {
        var chatrooms = new Map();
        for (const room of data.chats) {
          if (chatrooms.has(room.regardingid) && chatrooms.get(room.regardingid).ts > room.ts) {
            continue;
          }
          chatrooms.set(room.regardingid, room);
        }
        setChats(chatrooms);
        // console.log("chatrooms", chatrooms);
      });
    } catch (err) {
      console.log(err);
    }
  };

  const getGridActionsMenu = (item) => {
    const actions = [];

    if (item.status == "init" || item.status == "requested" || item.status == "active") {
      actions.push({
        label: (
          <Link to={`/workspace/edit-project?id=${item.id}`} style={{ color: "inherit" }}>
            Edit Project
          </Link>
        ),
        icon: <EditRoundedIcon fontSize={"small"} />,
      });
    }

    //only in initial,requested, the project can be published
    if (
      (item.status == "init" || item.status == "requested") &&
      ((item?.audiences && item.audiences.length < MAX_SELECTED_COUNT) || !item?.audiences)
    ) {
      actions.push({
        label: (
          <Link to={`/designers/publish-project?id=${item.id}`} style={{ color: "inherit" }}>
            {item.status == "init" ? (
              "Publish Project"
            ) : (
              <Tooltip title="Send your project to more manufacturers.">
                <SuiBox>More Requests</SuiBox>
              </Tooltip>
            )}
          </Link>
        ),
        icon: <CalendarTodayIcon fontSize={"small"} />,
      });
    }
    //all status can be duplicated
    // actions.push({
    //   label: (
    //     <Link to={`/workspace/duplicate-project?id=${item.id}`} style={{ color: "inherit" }}>
    //       Duplicate Project
    //     </Link>
    //   ),
    //   icon: <ContentCopyIcon fontSize={"small"} />,
    // });

    //only in initial,requested,canceled and  completed status, the project can be deleted.
    if (
      item.status == "init" ||
      item.status == "requested" ||
      item.status == "canceled" ||
      item.status == "completed"
    ) {
      actions.push({
        label: (
          <SuiTypography variant="body" color="error">
            Delete Project{" "}
          </SuiTypography>
        ),
        onClick: () => {
          setDeleteProjectVisible(true);
          setSelectedProject(item);
        },
        icon: <DeleteForeverRoundedIcon fontSize={"small"} color="error" />,
      });
    }
    return actions;
  };

  const handleFilterProjectStatus = (page, pageSize, str = searchStr, mySelectedStatus = null) => {
    let copyProjects = [...projectList];
    if (mySelectedStatus) {
      if (mySelectedStatus.length > 0) {
        copyProjects = copyProjects.filter((x) => mySelectedStatus.includes(x.status));
      }
    } else {
      if (selectedStatus.length > 0) {
        copyProjects = copyProjects.filter((x) => selectedStatus.includes(x.status));
      }
    }
    let filteredList = copyProjects.map((x) => {
      let StatusOpt = projectStatusOptions.find((opt) => x.status == opt.value);
      return { ...x, statusLabel: StatusOpt?.label ? StatusOpt.label : x.status };
    }); //all projects

    if (str) {
      const strUpper = str.toUpperCase();
      const searchFields = ["name", "description", "statusLabel"];
      filteredList = filteredList.filter((x) => {
        let result = false;
        for (let i = 0; i < searchFields.length; i++) {
          if (x[searchFields[i]]) {
            if (typeof x[searchFields[i]] == "string") {
              if (x[searchFields[i]].toUpperCase().includes(strUpper)) {
                result = true;
              }
            } else if (typeof x[searchFields[i]] == "object") {
              const text = JSON.stringify(x[searchFields[i]]);
              if (text.toUpperCase().includes(strUpper)) {
                result = true;
              }
            }
          }
        }
        return result;
      });
    }
    const totalNumber = filteredList.length;
    setTotalNumber(totalNumber);

    //filter based on pagination
    const sliceStart = pageSize * (page - 1);
    const sliceEnd = pageSize * page > totalNumber ? totalNumber : pageSize * page;
    const newFilteredList = filteredList.slice(sliceStart, sliceEnd);
    setFilteredProjectList(newFilteredList);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    handleFilterProjectStatus(newPage, pageSize, searchStr);
  };

  const handleChangeRowsPerPage = (option) => {
    const newPageSize = parseInt(option.value, 10);
    setPageSize(newPageSize);
    setPage(1);
    handleFilterProjectStatus(1, newPageSize, searchStr);
  };

  const pageCount = parseInt(Math.ceil(totalNumber / pageSize), 10);
  const pageOptions = [];
  for (let i = 0; i < pageCount; i++) {
    pageOptions.push(i);
  }
  const entriesStart = pageSize * (page - 1) + 1;
  const entriesEnd = pageSize * page > totalNumber ? totalNumber : pageSize * page;

  const handleSearchChange = (str) => {
    setSearchStr(str);
    handleFilterProjectStatus(page, pageSize, str);
  };

  const handleOnSortChange = (e) => {
    setOrderOpt(e);
    let sortStr = e?.value;

    let sortedProjects = [];
    //project status:init,requested,active,completed,canceled=>draft/pending/ongoing/completed/canceled
    //we should sort them by this order instead of alphabet order
    if (sortStr == "status") {
      sortedProjects = sortByProjectStatus(projectList, sortKeys);
    } else if (sortStr == "-status") {
      sortedProjects = sortByProjectStatus(projectList, sortKeys_reverse);
    } else {
      sortedProjects = projectList.sort((a, b) => compare(a, b, sortStr));
    }

    const newFilteredList = sortedProjects.slice(0, pageSize);
    setPage(1);
    setFilteredProjectList(newFilteredList);
  };

  const sortByProjectStatus = (projectList, sortKeys) => {
    let newList = [];
    for (let i = 0; i < sortKeys.length; i++) {
      for (let j = 0; j < projectList.length; j++) {
        if (projectList[j].status == sortKeys[i]) {
          newList.push(projectList[j]);
        }
      }
    }
    return newList;
  };

  const handleViewChange = (e, value) => {
    if (value !== null) {
      setView(value);
    }
  };

  const formatProjectStatus = (type, row) => {
    const option = projectStatusOptions.find((x) => x.value == type);
    if (option) {
      if (option.value == "init") {
        return <SuiBadgeDot color="warning" badgeContent={option.label} />;
      } else if (option.value == "requested") {
        return <SuiBadgeDot size="xs" color="info" badgeContent={option.label} />;
      } else if (option.value == "active") {
        return <SuiBadgeDot size="xs" color="success" badgeContent={option.label} />;
      } else if (option.value == "canceled") {
        return <SuiBadgeDot color="error" badgeContent={option.label} />;
      } else if (option.value == "completed") {
        return <SuiBadgeDot color="dark" badgeContent={option.label} />;
      } else if (row?.original?.deleted_at) {
        return (
          <Tooltip
            title={`Deleted at ${moment(row.original.deleted_at).format("YYYY-MM-DD")}`}
            placement="top"
          >
            <SuiBadgeDot color="error" badgeContent={"Deleted"} />
          </Tooltip>
        );
      } else {
        return <SuiBadgeDot color="secondary" badgeContent={option.label} />;
      }
    } else {
      return type;
    }
  };

  const getProjectTableRow = (item) => {
    const quote = item.quote ? item.quote : {};
    const products = item.products || [];
    let styleImages = [];
    //add style first image
    for (let i = 0; i < products.length; i++) {
      let temp = getStyleImages(products[i], false);
      styleImages.push(...temp);
    }
    //add all project reference image
    const fileList = item?.metadata?.othersFiles || [];
    const files = fileList.filter((f) => f?.type && f.type.includes("image"));
    if (files && files.length > 0) {
      styleImages.push(...files);
    }
    if (styleImages.length == 0) {
      styleImages = [{ url: curved9, name: "deafult_image" }];
    }

    const actions = [];
    actions.push(
      <Tooltip title="View Details">
        <SuiBox
          component={Link}
          to={
            account.account_type == "de"
              ? `/produce/all-projects?id=${item.id}`
              : `/job/requests?id=${item.id}`
          }
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <ManageSearchIcon fontSize={"small"} color="dark" />
        </SuiBox>
      </Tooltip>
    );

    if (item.status == "init" || item.status == "requested" || item.status == "active") {
      actions.push(
        <Tooltip title="Edit Project">
          <SuiBox
            component={Link}
            to={`/workspace/edit-project?id=${item.id}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <EditRoundedIcon fontSize={"small"} color="dark" />
          </SuiBox>
        </Tooltip>
      );
    }

    //only in initial,requested, the project can be published
    if (
      (item.status == "init" || item.status == "requested") &&
      ((item?.audiences && item.audiences.length < MAX_SELECTED_COUNT) || !item?.audiences)
    ) {
      actions.push(
        <Tooltip title="Send your project to more manufacturers.">
          <SuiBox
            component={Link}
            to={`/designers/publish-project?id=${item.id}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <CalendarTodayIcon fontSize={"small"} color="dark" />
          </SuiBox>
        </Tooltip>
      );
    }

    //all status can be duplicated

    // actions.push(
    //   <Tooltip title="Duplicate">
    //     <SuiBox
    //       component={Link}
    //       to={`/workspace/duplicate-project?id=${item.id}`}
    //       onClick={(e) => {
    //         e.stopPropagation();
    //       }}
    //     >
    //       <ContentCopyIcon fontSize={"small"} color="dark" />
    //     </SuiBox>
    //   </Tooltip>
    // );

    //only in initial,requested,canceled and  completed status, the project can be deleted.
    if (
      item.status == "init" ||
      item.status == "requested" ||
      item.status == "canceled" ||
      item.status == "completed"
    ) {
      actions.push(
        <Tooltip title="Delete">
          <SuiBox>
            <DeleteForeverRoundedIcon
              fontSize={"small"}
              color="error"
              sx={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteProjectVisible(true);
                setSelectedProject(item);
                retrieveProjects(stage);
              }}
            />{" "}
          </SuiBox>
        </Tooltip>
      );
    }

    const actionsComponent = (
      <SuiBox display="flex" alignItems="center" justifyContent="center">
        {actions.map((x, index) => (
          <SuiBox display="flex" alignItems="center" justifyContent="center">
            {" "}
            <SuiBox mx={0.2} color="secondary">
              {index == 0 ? null : "|"}
            </SuiBox>
            {x}
          </SuiBox>
        ))}
      </SuiBox>
    );

    var room = chats && chats.has(item.id) ? chats.get(item.id) : null;

    const ownerStore =
      item?.owner?.stores && item.owner.stores.length > 0 ? item.owner.stores[0] : {};
    const ownerName = item?.store_name ? item?.store_name : ownerStore?.name;
    const DesignerCom =
      account.account_type == "ma" ? (
        <SuiBox
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          gap={1}
          sx={{ maxWidth: 260 }}
        >
          <SuiBox>
            <Tooltip position="top" title={ownerName}>
              <SuiAvatar
                src={ownerStore?.metadata?.avatar}
                alt="profile-image"
                variant="rounded"
                size="sm"
                shadow="sm"
              />
            </Tooltip>
          </SuiBox>
          <SuiBox>
            <SuiTypography variant="button" fontWeight="regular" textTransform="capitalize">
              {ownerName}
            </SuiTypography>
          </SuiBox>
        </SuiBox>
      ) : null;
    return {
      key: item.id,
      image: styleImages[0],
      name: item.name,
      budget: quote.budget,
      budget_min: quote.budget_min,
      budget_max: quote.budget_max,
      desired_end_date: quote.desired_end_date,
      created_at: item.created_at,
      deleted_at: item.deleted_at,
      updated_at: item.updated_at,
      status: item.status,
      action: actionsComponent,
      room: room,
      DesignerCom: DesignerCom,
    };
  };

  const columns = [
    {
      Header: "Image",
      accessor: "image",
      align: "center",
      Cell: ({ row, value }) => {
        return (
          <Link to={`/produce/all-projects?id=${row?.original?.key}`}>
            <ImageCardWrapper data={value} height={60} width={60} iconMode />
          </Link>
        );
      },
    },
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ row, value }) => {
        return <div className="max-w-[200px]">{value}</div>;
      },
    },
    {
      Header: "status",
      accessor: "status",
      Cell: ({ row, value }) => formatProjectStatus(value, row),
    },
    {
      Header: "Budget",
      accessor: "budget",
      Cell: ({ row }) => {
        return (
          <div className="flex flex-nowrap">
            <NumberFormat
              value={row?.original?.budget_min}
              displayType={"text"}
              thousandSeparator={true}
              prefix={"$"}
              renderText={(value, props) => (
                <SuiTypography variant="body1" fontWeight="regular" color="text" {...props}>
                  {value}
                </SuiTypography>
              )}
            />
            ~{" "}
            <NumberFormat
              value={row?.original?.budget_max}
              displayType={"text"}
              thousandSeparator={true}
              prefix={"$"}
              renderText={(value, props) => (
                <SuiTypography variant="body1" fontWeight="regular" color="text" {...props}>
                  {value}
                </SuiTypography>
              )}
            />
          </div>
        );
      },
    },
    {
      Header: "Desired End Date",
      accessor: "desired_end_date",
      Cell: ({ row, value }) => {
        return <div>{moment(value).format("MM/DD/YY")}</div>;
      },
    },
    {
      Header: "Updated Date",
      accessor: "updated_at",
      Cell: ({ row, value }) => {
        return <div>{moment(value).format("MM/DD/YY")}</div>;
      },
    },
    {
      Header: "More",
      isSorted: false,
      accessor: "action",
    },
    {
      Header: "Message",
      accessor: "room",

      Cell: ({ row, value }) => {
        const room = value;
        return room ? (
          <SuiBox className="flex items-center" sx={{ maxWidth: 260 }}>
            <SuiBadge variant="dot" color="error" invisible={room.ts <= room.viewts}>
              <ContactAvatar room={room} />
            </SuiBadge>
            <div className="w-full">
              <div className="flex justify-between items-center">
                <SuiTypography
                  variant="body1"
                  fontWeight="bold"
                  textTransform="capitalize"
                  className={`block ml-2 text-gray-600 font-semibold`}
                >
                  {room.name}
                </SuiTypography>
                <SuiTypography variant="caption" className={`block ml-2 text-gray-600`}>
                  {smartdate.format(new Date(room.ts))}
                </SuiTypography>
              </div>
              <div className="flex justify-between">
                <SuiTypography
                  className={`ml-2 text-gray-600`}
                  variant="caption"
                  textTransform="none"
                  color="secondary"
                  lineHeight="1.2"
                  sx={{
                    width: "100%",
                    minHeight: "28px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: "2",
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {room.lastcontent}
                </SuiTypography>
              </div>
            </div>
          </SuiBox>
        ) : (
          row?.original?.DesignerCom
        );
      },
    },
  ];

  const rows = filteredProjectList.map((item) => getProjectTableRow(item));

  const handleRowClick = (row) => {
    let link =
      account.account_type == "de"
        ? `/produce/all-projects?id=${row?.original?.key}`
        : `/job/requests?id=${row?.original?.key}`;

    navigate(link);
    //window.open(`/workspace/styles-master?id=${row?.original?.key}`, "_blank");
  };

  const handleChangeProjectStatus = (e) => {
    const selectedValue = e?.target?.value || "";
    if (selectedValue && selectedStatus.includes(selectedValue)) {
      let newSelectedStatus = selectedStatus.filter((x) => x != selectedValue);
      setSelectedStatus(newSelectedStatus);
      handleFilterProjectStatus(1, pageSize, searchStr, newSelectedStatus);
    } else if (selectedValue && !selectedStatus.includes(selectedValue)) {
      let newSelectedStatus = [...selectedStatus, selectedValue];
      setSelectedStatus(newSelectedStatus);
      handleFilterProjectStatus(1, pageSize, searchStr, newSelectedStatus);
    }
    setPage(1);
  };
  const getTooltipForStatus = (status) => {
    let record = explanation.find((exp) => exp.stage == status.value);
    return record?.text ? record.text : status.label;
  };

  if (projectId) {
    return <ProjectList />;
  }

  return (
    <SuiBox>
      <SuiBox pb={3}>
        {/* <SuiBox>
          <SuiBox
            display="flex"
            justifyContent="flex-start"
            alignItems="flex-end"
            gap={1}
            sx={{ ml: 2 }}
          >
            <SuiTypography variant="h5" textTransform="capitalize">
              {hint?.title ? hint.title : "Project Board"}
            </SuiTypography>
            {hint?.text ? (
              <SuiBox ml={0.5} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="regular"
                  color="text"
                >
                  {hint.text}
                </SuiTypography>
              </SuiBox>
            ) : (
              <SuiBox my={1} />
            )}
          </SuiBox>
        </SuiBox> */}

        <SuiBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          my={1}
          sx={{ flexWrap: "wrap" }}
        >
          <SuiBox display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
            <Link to={`/produce/all-projects/create`}>
              <SuiButton variant="gradient" color={"dark"}>
                Create Project
              </SuiButton>
            </Link>
            <SuiBox>
              {stage != "prospects" ? (
                <SuiBox>
                  <ButtonGroup
                    variant="outlined"
                    aria-label="text button group"
                    color="dark"
                    onClick={handleChangeProjectStatus}
                  >
                    {projectStatusOptions.map((x) => (
                      <Tooltip title={getTooltipForStatus(x)}>
                        <SuiButton
                          variant={selectedStatus.includes(x.value) ? "contained" : "outlined"}
                          value={x.value}
                          color={selectedStatus.includes(x.value) ? "info" : "dark"}
                          size="small"
                          sx={{ px: 1, fontSize: 11 }}
                        >
                          {x.label}({stagesProjects[x.value] ? stagesProjects[x.value].length : 0})
                        </SuiButton>
                      </Tooltip>
                    ))}
                  </ButtonGroup>
                </SuiBox>
              ) : (
                <SuiBox />
              )}
            </SuiBox>
          </SuiBox>

          <SuiBox display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
            <SuiBox>
              <Card sx={{ borderRadius: 2, maxWidth: 145 }}>
                <SuiSelect
                  options={projectSortOptions}
                  placeholder="Sort"
                  onChange={handleOnSortChange}
                  value={orderOpt}
                />
              </Card>
            </SuiBox>

            <SuiBox>
              <SuiInput
                placeholder="Search Keywords"
                type="text"
                onChange={(e) => handleSearchChange(e?.target?.value)}
                icon={{
                  direction: "right",
                  component: (
                    <SearchIcon
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleSearchChange(searchStr)}
                    />
                  ),
                }}
              />
            </SuiBox>
            <SuiBox display="flex" alignItems="center" sx={{ gap: 1 }}>
              <ToggleButtonGroup value={view} exclusive onChange={handleViewChange}>
                <Tooltip title="List View">
                  <ToggleButton value="table">
                    <TableRowsIcon fontSize="sm" color="inherit" />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title="Card View">
                  <ToggleButton value="grid">
                    <GridViewIcon fontSize="sm" />
                  </ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>
            </SuiBox>
          </SuiBox>
        </SuiBox>
        <div>
          {view == "grid" ? (
            filteredProjectList && filteredProjectList.length > 0 ? (
              filteredProjectList.map((x, index) => (
                <ProjectCard2
                  key={index}
                  detailUrl={`/produce/all-projects?id=${x.id}`}
                  project={x}
                  chats={chats}
                  removeProspect={null}
                  actions={<Actionables actions={getGridActionsMenu(x)} size="small" />}
                />
              ))
            ) : (
              <EmptyProjectCard />
            )
          ) : rows.length > 0 ? (
            <DataTable
              table={{ columns, rows: rows }}
              entriesPerPage={{ defaultValue: pageSize }}
              showTotalEntries={false}
              pagination={false}
              noEndBorder
              handleRowClick={handleRowClick}
              isSorted={false}
            />
          ) : (
            <EmptyProjectCard />
          )}
        </div>

        <SuiBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={3}
        >
          <div className="flex items-center">
            <SuiBox mb={{ xs: 3, sm: 0 }}>
              <SuiTypography variant="button" color="secondary" fontWeight="regular">
                Showing {entriesStart} to {entriesEnd} of {totalNumber} entries
              </SuiTypography>
            </SuiBox>

            <SuiBox display="flex" alignItems="center" gap={1}>
              <SuiSelect
                defaultValue={{ value: pageSize, label: pageSize }}
                options={[10, 20, 50].map((entry) => ({ value: entry, label: entry }))}
                onChange={handleChangeRowsPerPage}
                size="small"
              />

              <SuiTypography variant="caption" color="secondary">
                entries per page
              </SuiTypography>
            </SuiBox>
          </div>
          <Pagination
            variant={"outlined"}
            color={"info"}
            count={pageOptions.length}
            defaultPage={page}
            page={page}
            onChange={handleChangePage}
          />
        </SuiBox>
      </SuiBox>

      {/**Send request */}
      {selectedProject && sendRequestVisible ? (
        <SendRequestModal
          heading="Send Notifications"
          visible={sendRequestVisible}
          okText="Send"
          okColor="dark"
          handleOk={() => handleSendRequestOk()}
          handleClose={() => setSendRequestVisible(false)}
        />
      ) : null}
      {/**Delete project */}
      {selectedProject && deleteProjectVisible ? (
        <Prompt
          heading="Delete Project"
          text={`Are you sure you want to delete project: ${selectedProject.name}`}
          visible={deleteProjectVisible}
          okText="Delete"
          okColor="error"
          handleOk={() =>
            api.project
              .delete(selectedProject.id)
              .then(() => {
                toast.success(`Delete project ${selectedProject.name} successfully!`);
                handleRefresh();
                setDeleteProjectVisible(false);
              })
              .catch((err) => {
                console.log(err);
                toast.error(`Delete project ${selectedProject.name} failed!`);
              })
          }
          handleClose={() => setDeleteProjectVisible(false)}
        />
      ) : null}
    </SuiBox>
  );
};

export default ProjectHome;
